/**
 * Frame Bed Mattress Upsell
 * Handles mattress popup, tab switching, variant matching, and pricing
 */

class FramebedMattressUpsell {
    constructor() {
        this.mattressData = {};
        this.productCache = {};
        this.currentTab = 'orthopedic';
        this.selectedBedSize = null;
        this.currencyFormat = '£{{amount}}';

        this.init();
    }

    init() {


        // Load mattress product handles from JSON
        this.loadMattressData();

        // Set up event listeners
        this.setupEventListeners();

        // Get initial bed size
        this.updateSelectedBedSize();


    }

    loadMattressData() {
        const dataElement = document.getElementById('mattress-products-data');
        if (dataElement) {
            try {
                this.mattressData = JSON.parse(dataElement.textContent);

            } catch (e) {

            }
        }
    }

    setupEventListeners() {
        // Open popup button
        const openBtn = document.getElementById('open-mattress-popup');
        if (openBtn) {
            openBtn.addEventListener('click', () => this.openPopup());
        }

        // Close popup button
        const closeBtn = document.getElementById('close-mattress-popup');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => this.closePopup());
        }

        // Close on overlay click
        const overlay = document.getElementById('mattress-popup-overlay');
        if (overlay) {
            overlay.addEventListener('click', (e) => {
                if (e.target === overlay) {
                    this.closePopup();
                }
            });
        }

        // ESC key to close
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                this.closePopup();
            }
        });

        // Tab buttons
        const tabButtons = document.querySelectorAll('.mattress-tab-btn');
        tabButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                const tab = e.target.dataset.tab;
                this.switchTab(tab);
            });
        });

        // Listen for bed size changes
        const sizeSelectors = document.querySelectorAll('.js-single-option-selector');
        sizeSelectors.forEach(selector => {
            selector.addEventListener('change', () => this.handleBedSizeChange());
        });

        // Listen to variant select dropdown
        const section = document.querySelector('[data-section-type="product-template"]');
        const sectionId = section ? section.dataset.sectionId : null;
        if (sectionId) {
            const variantSelect = document.querySelector('.js-product-select--' + sectionId);
            if (variantSelect) {
                variantSelect.addEventListener('change', () => this.handleBedSizeChange());
            }
        }
    }

    openPopup() {
        const overlay = document.getElementById('mattress-popup-overlay');
        if (overlay) {
            overlay.style.display = 'flex';
            document.body.classList.add('mattress-popup-open');

            // Load mattresses for current tab
            this.loadMattressesForTab(this.currentTab);
        }
    }

    closePopup() {
        const overlay = document.getElementById('mattress-popup-overlay');
        if (overlay) {
            overlay.style.display = 'none';
            document.body.classList.remove('mattress-popup-open');
        }
    }

    switchTab(tabName) {
        this.currentTab = tabName;

        // Update tab buttons
        const tabButtons = document.querySelectorAll('.mattress-tab-btn');
        tabButtons.forEach(btn => {
            if (btn.dataset.tab === tabName) {
                btn.classList.add('active');
            } else {
                btn.classList.remove('active');
            }
        });

        // Update tab content
        const tabContents = document.querySelectorAll('.mattress-tab-content');
        tabContents.forEach(content => {
            if (content.id === 'tab-' + tabName) {
                content.classList.add('active');
            } else {
                content.classList.remove('active');
            }
        });

        // Load mattresses for this tab
        this.loadMattressesForTab(tabName);
    }

    async loadMattressesForTab(tabName) {
        const gridId = 'mattress-grid-' + tabName;
        const grid = document.getElementById(gridId);
        if (!grid) return;

        // Show loading
        grid.innerHTML = '<div class="mattress-loading">Loading mattresses...</div>';

        // Get product handles for this tab
        const handles = this.mattressData[tabName];
        if (!handles || handles.length === 0) {
            grid.innerHTML = '<div class="mattress-loading">No mattresses available</div>';
            return;
        }

        // Fetch all products
        const products = await this.fetchProducts(handles);

        // Render products
        this.renderMattresses(grid, products);
    }

    async fetchProducts(handles) {
        const products = [];

        for (const handle of handles) {
            // Check cache first
            if (this.productCache[handle]) {
                products.push(this.productCache[handle]);
                continue;
            }

            // Fetch from Shopify
            try {
                const response = await fetch(`/products/${handle}.js`);
                if (response.ok) {
                    const product = await response.json();
                    this.productCache[handle] = product;
                    products.push(product);
                } else {

                }
            } catch (error) {

            }
        }

        return products;
    }

    renderMattresses(grid, products) {
        if (products.length === 0) {
            grid.innerHTML = '<div class="mattress-loading">No mattresses available</div>';
            return;
        }

        grid.innerHTML = '';

        products.forEach(product => {
            const card = this.createMattressCard(product);
            grid.appendChild(card);
        });
    }

    createMattressCard(product) {
        const card = document.createElement('div');
        card.className = 'mattress-card';

        // Find matching variant for selected bed size
        const matchingVariant = this.findMatchingVariant(product);

        // Get image
        const imageUrl = product.featured_image || (product.images && product.images[0]) || '';

        // Calculate savings
        const savings = this.calculateSavings(matchingVariant);

        card.innerHTML = `
      <img src="${imageUrl}" alt="${product.title}" class="mattress-card__image" loading="lazy">
      <div class="mattress-card__content">
        <h3 class="mattress-card__title">${product.title}</h3>
        ${matchingVariant ? `
          <div class="mattress-card__price">${this.formatMoney(matchingVariant.price)}</div>
          ${savings.hasSavings ? `
            <div class="mattress-card__savings">
              You save ${this.formatMoney(savings.amount)} (${savings.percent}%)
            </div>
          ` : ''}
          <button type="button" 
            class="mattress-card__button" 
            data-variant-id="${matchingVariant.id}"
            data-product-title="${product.title}"
            onclick="window.framebedMattressUpsell.addMattressToBed(${matchingVariant.id}, '${product.title}', ${matchingVariant.price})">
            Add to Bed
          </button>
        ` : `
          <div class="mattress-card__error">
            Not available in ${this.selectedBedSize || 'selected size'}
          </div>
          <button type="button" class="mattress-card__button" disabled>
            Unavailable
          </button>
        `}
      </div>
    `;

        return card;
    }

    findMatchingVariant(product) {
        if (!this.selectedBedSize || !product.variants) {
            return product.variants ? product.variants[0] : null;
        }

        // Try to find variant matching bed size
        const matchingVariant = product.variants.find(variant => {
            // Check all options for a match
            return variant.option1 === this.selectedBedSize ||
                variant.option2 === this.selectedBedSize ||
                variant.option3 === this.selectedBedSize;
        });

        return matchingVariant || null;
    }

    calculateSavings(variant) {
        if (!variant || !variant.compare_at_price || variant.compare_at_price <= variant.price) {
            return { hasSavings: false, amount: 0, percent: 0 };
        }

        const savingAmount = variant.compare_at_price - variant.price;
        const savingPercent = Math.round((savingAmount / variant.compare_at_price) * 100);

        return {
            hasSavings: true,
            amount: savingAmount,
            percent: savingPercent
        };
    }

    handleBedSizeChange() {
        this.updateSelectedBedSize();

        // Reload current tab if popup is open
        const overlay = document.getElementById('mattress-popup-overlay');
        if (overlay && overlay.style.display !== 'none') {
            this.loadMattressesForTab(this.currentTab);
        }
    }

    updateSelectedBedSize() {
        // Try to get from product JSON and selected variant
        const section = document.querySelector('[data-section-type="product-template"]');
        const sectionId = section ? section.dataset.sectionId : null;

        if (sectionId) {
            const variantSelect = document.querySelector('.js-product-select--' + sectionId);
            if (variantSelect && variantSelect.value) {
                const selectedVariantId = parseInt(variantSelect.value);

                // Get product JSON
                const productJsonElement = document.getElementById('ProductJson-' + sectionId);
                if (productJsonElement) {
                    try {
                        const productData = JSON.parse(productJsonElement.textContent);
                        const selectedVariant = productData.variants.find(v => v.id === selectedVariantId);

                        if (selectedVariant) {
                            // Get the size from variant options (usually option1, but could be any)
                            this.selectedBedSize = selectedVariant.option1 || selectedVariant.option2 || selectedVariant.option3;

                            return;
                        }
                    } catch (e) {

                    }
                }
            }
        }

        // Fallback: try to get from visible option selectors
        const sizeLabel = document.querySelector('.variants__label span');
        if (sizeLabel) {
            this.selectedBedSize = sizeLabel.textContent.trim();

        }
    }

    addMattressToBed(variantId, productTitle, variantPrice) {
        // Store values in hidden fields (defined in single-form-headboard.liquid)
        document.getElementById('mattress-addon-id').value = variantId;
        document.getElementById('mattress-addon-title').value = productTitle;
        document.getElementById('mattress-addon-price').value = variantPrice;

        this.closePopup();
        this.showConfirmation(productTitle);

        // Notify other scripts
        document.dispatchEvent(new CustomEvent('mattress-addon-updated', {
            detail: { price: variantPrice, variantId: variantId, title: productTitle }
        }));
    }

    showConfirmation(productTitle) {
        // Update the upsell button text to show selection
        const button = document.getElementById('open-mattress-popup');
        if (button) {
            button.textContent = `✓ ${productTitle}`;
            button.style.background = '#4CAF50';
            button.style.color = '#fff';
        }
    }

    formatMoney(amountInPence) {
        const amount = (amountInPence / 100).toFixed(2);
        return this.currencyFormat.replace('{{amount}}', amount);
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        if (document.getElementById('mattress-products-data')) {
            window.framebedMattressUpsell = new FramebedMattressUpsell();
        }
    });
} else {
    if (document.getElementById('mattress-products-data')) {
        window.framebedMattressUpsell = new FramebedMattressUpsell();
    }
}
